import React, { useState, useEffect, useCallback } from 'react';
import { Receipt, RotateCcw, Search, AlertTriangle, Loader2, Ticket, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { api } from '../../App';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const money = (n) => `£${Number(n || 0).toFixed(2)}`;
const STATUS_BADGE = {
  paid: 'bg-emerald-100 text-emerald-800', partially_refunded: 'bg-amber-100 text-amber-800', refunded: 'bg-slate-200 text-slate-800',
  pending: 'bg-slate-100 text-slate-600', waived: 'bg-blue-100 text-blue-800', amount_mismatch: 'bg-red-100 text-red-800',
};
const SERVICES = [['', 'All services'], ['membership', 'Membership'], ['ticket_purchase', 'Event tickets'], ['donation', 'Donations'], ['saturday_club', 'Saturday Club']];
const STATUSES = [['', 'Any status'], ['paid', 'Paid'], ['partially_refunded', 'Partially refunded'], ['refunded', 'Refunded'], ['pending', 'Pending / abandoned']];

/* ============================================================
   PAYMENTS & REFUNDS TAB (spec P4.4) — view: admin/treasurer/chairman; refund: admin + treasurer (server-enforced)
   ============================================================ */
export function RefundsTab({ token, role }) {
  const canRefund = ['admin', 'treasurer'].includes(role);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ gross: 0, refunded: 0, net: 0 });
  const [loading, setLoading] = useState(true);
  const [service, setService] = useState('');
  const [status, setStatus] = useState('paid');
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [selected, setSelected] = useState(null);   // session_id being refunded

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (service) p.set('service', service); if (status) p.set('status', status); if (q) p.set('q', q); if (from) p.set('date_from', from); if (to) p.set('date_to', to);
      const d = await api.getAuth(`/api/admin/payments?${p.toString()}`, token);
      setRows(d.payments || []); setTotals(d.totals || {});
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [token, service, status, q, from, to]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="refunds-tab">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
        <div>
          <h2 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Payments & Refunds</h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Every Stripe-confirmed payment across memberships, tickets, donations and Saturday Club. {canRefund ? 'Refunds go back to the original card via Stripe.' : 'Your role can view payments; refunds are for Admin and Treasurer.'}</p>
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-4 mb-5">
        <Card><CardContent className="p-5"><div className="text-sm text-[hsl(var(--muted-foreground))] mb-1">Gross paid (filtered)</div><div className="text-3xl font-bold tabular-nums" style={{ fontFamily: 'Space Grotesk' }} data-testid="payments-gross">{money(totals.gross)}</div></CardContent></Card>
        <Card><CardContent className="p-5"><div className="text-sm text-[hsl(var(--muted-foreground))] mb-1">Refunded</div><div className="text-3xl font-bold tabular-nums text-amber-700" style={{ fontFamily: 'Space Grotesk' }} data-testid="payments-refunded">{money(totals.refunded)}</div></CardContent></Card>
        <Card><CardContent className="p-5"><div className="text-sm text-[hsl(var(--muted-foreground))] mb-1">Net</div><div className="text-3xl font-bold tabular-nums text-emerald-700" style={{ fontFamily: 'Space Grotesk' }} data-testid="payments-net">{money(totals.net)}</div></CardContent></Card>
      </div>

      <div className="flex flex-col lg:flex-row gap-2 mb-4">
        <select className="h-9 rounded-md border border-input bg-transparent px-3 text-sm" value={service} onChange={e => setService(e.target.value)} data-testid="payments-filter-service">{SERVICES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        <select className="h-9 rounded-md border border-input bg-transparent px-3 text-sm" value={status} onChange={e => setStatus(e.target.value)} data-testid="payments-filter-status">{STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        <Input type="date" value={from} onChange={e => setFrom(e.target.value)} className="h-9 sm:max-w-[170px]" aria-label="From date" data-testid="payments-filter-from" />
        <Input type="date" value={to} onChange={e => setTo(e.target.value)} className="h-9 sm:max-w-[170px]" aria-label="To date" data-testid="payments-filter-to" />
        <div className="relative flex-1"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" /><Input className="pl-9 h-9" placeholder="Name, email, event, reference or Stripe session" value={q} onChange={e => setQ(e.target.value)} data-testid="payments-search" /></div>
      </div>

      {loading ? <div className="animate-pulse h-48 bg-[hsl(var(--muted))] rounded-xl" /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b">
              {['Date', 'Service', 'Paid by', 'Paid', 'Refunded', 'Remaining', 'Status', ''].map(h => <th key={h} className="text-left p-3 font-medium text-[hsl(var(--muted-foreground))]">{h}</th>)}
            </tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.session_id} className="border-b hover:bg-[hsl(var(--muted))]" data-testid={`payment-row-${r.session_id}`}>
                  <td className="p-3 text-[hsl(var(--muted-foreground))] whitespace-nowrap">{fmtDate(r.created_at)}</td>
                  <td className="p-3"><div>{r.service}</div>{r.entity?.ref && <div className="text-xs text-[hsl(var(--muted-foreground))] max-w-[180px] truncate">{r.entity.ref}</div>}</td>
                  <td className="p-3"><div>{r.entity?.name || '—'}</div><div className="text-xs text-[hsl(var(--muted-foreground))]">{r.entity?.email}</div></td>
                  <td className="p-3 font-semibold tabular-nums">{money(r.amount)}</td>
                  <td className="p-3 tabular-nums text-amber-700">{r.refunded ? money(r.refunded) : '—'}</td>
                  <td className="p-3 tabular-nums">{money(r.remaining)}</td>
                  <td className="p-3"><Badge className={`text-xs ${STATUS_BADGE[r.payment_status] || 'bg-slate-100'}`}>{r.payment_status.replace('_', ' ')}</Badge></td>
                  <td className="p-3 text-right">
                    <Button size="sm" variant="outline" onClick={() => setSelected(r.session_id)} data-testid={`payment-open-${r.session_id}`}><Receipt className="w-3.5 h-3.5 mr-1" /> {canRefund && r.remaining > 0 && r.raw_status === 'paid' ? 'Refund / details' : 'Details'}</Button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-[hsl(var(--muted-foreground))]">No payments match these filters</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {selected && <RefundDialog sessionId={selected} token={token} canRefund={canRefund} onClose={() => setSelected(null)} onDone={() => { setSelected(null); load(); }} />}
    </div>
  );
}

function RefundDialog({ sessionId, token, canRefund, onClose, onDone }) {
  const [detail, setDetail] = useState(null);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [ticketIds, setTicketIds] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.getAuth(`/api/admin/payments/${sessionId}`, token).then(d => { setDetail(d); setAmount(String(d.payment.remaining || '')); }).catch(e => toast.error(e.message));
  }, [sessionId, token]);

  const toggleTicket = (id, price) => {
    setTicketIds(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      // suggest amount = sum of selected ticket prices (capped at remaining), admin can still edit
      const sum = (detail?.tickets || []).filter(t => next.includes(t.ticket_id)).reduce((a, t) => a + Number(t.price || 0), 0);
      if (next.length && detail) setAmount(String(Math.min(sum, detail.payment.remaining).toFixed(2)));
      return next;
    });
  };

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.postAuth('/api/admin/refunds', { session_id: sessionId, amount: Number(amount), reason, confirm, ticket_ids: ticketIds.length ? ticketIds : null }, token);
      toast.success(r.message);
      onDone();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const p = detail?.payment;
  const refundable = p && p.raw_status === 'paid' && p.remaining > 0;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="refund-dialog">
        <DialogHeader>
          <DialogTitle>{p ? `${p.service} payment — ${money(p.amount)}` : 'Payment'}</DialogTitle>
          <DialogDescription>{p ? <>{p.entity?.name} · {p.entity?.email} · {fmtDate(p.created_at)} · <span className="font-mono text-xs break-all">{p.session_id}</span></> : 'Loading…'}</DialogDescription>
        </DialogHeader>
        {!detail ? <div className="animate-pulse h-32 bg-[hsl(var(--muted))] rounded-xl" /> : (
          <div className="space-y-5">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="p-3 rounded-lg border"><div className="text-xs text-[hsl(var(--muted-foreground))]">Paid</div><div className="font-bold tabular-nums">{money(p.amount)}</div></div>
              <div className="p-3 rounded-lg border"><div className="text-xs text-[hsl(var(--muted-foreground))]">Previously refunded</div><div className="font-bold tabular-nums text-amber-700" data-testid="refund-previously">{money(p.refunded)}</div></div>
              <div className="p-3 rounded-lg border"><div className="text-xs text-[hsl(var(--muted-foreground))]">Remaining refundable</div><div className="font-bold tabular-nums text-emerald-700" data-testid="refund-remaining">{money(p.remaining)}</div></div>
            </div>

            {detail.tickets?.length > 0 && (
              <div>
                <Label className="flex items-center gap-1"><Ticket className="w-4 h-4" /> Tickets in this order {canRefund && refundable && <span className="text-xs text-[hsl(var(--muted-foreground))]">— tick the ones this refund covers</span>}</Label>
                <div className="mt-2 space-y-1">
                  {detail.tickets.map(t => (
                    <label key={t.ticket_id} className={`flex items-center gap-3 text-sm p-2 rounded-md border ${t.status === 'refunded' ? 'opacity-60' : 'cursor-pointer hover:bg-[hsl(var(--muted))]'}`} data-testid={`refund-ticket-${t.ticket_id}`}>
                      {canRefund && refundable && t.status !== 'refunded' && t.status !== 'cancelled' && <Checkbox checked={ticketIds.includes(t.ticket_id)} onCheckedChange={() => toggleTicket(t.ticket_id, t.price)} />}
                      <span className="flex-1">{t.attendee_name} <span className="text-[hsl(var(--muted-foreground))]">· {t.ticket_type}{t.event_day ? ` · ${fmtDate(t.event_day)}` : ''}</span></span>
                      <span className="tabular-nums">{money(t.price)}</span>
                      <Badge variant="outline" className="text-[10px] capitalize">{t.status}</Badge>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-start gap-2 text-xs text-[hsl(var(--muted-foreground))] p-3 rounded-md bg-[hsl(var(--muted))]"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />{detail.entitlement_note}</div>

            {detail.refunds?.length > 0 && (
              <div>
                <Label>Refund history</Label>
                <div className="mt-1 space-y-1">
                  {detail.refunds.map(r => (
                    <div key={r.refund_id} className="flex flex-wrap items-center justify-between gap-2 text-sm border rounded-md px-3 py-2" data-testid={`refund-history-${r.refund_id}`}>
                      <span><strong className="tabular-nums">{money(r.amount)}</strong> · {r.reason}</span>
                      <span className="text-xs text-[hsl(var(--muted-foreground))]">{fmtDate(r.created_at)} · {r.created_by} · <Badge variant="outline" className="text-[10px] capitalize">{r.status}</Badge>{r.failure_reason && <span className="text-red-700"> {r.failure_reason}</span>}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {canRefund && refundable && (
              <div className="space-y-3 border-t pt-4">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="rf-amount">Refund amount (£)</Label>
                    <Input id="rf-amount" type="number" min="0.5" step="0.01" max={p.remaining} value={amount} onChange={e => setAmount(e.target.value)} data-testid="refund-amount-input" />
                    <div className="flex gap-2 mt-1"><Button type="button" size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setAmount(String(p.remaining))}>Full remaining</Button><Button type="button" size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setAmount((p.remaining / 2).toFixed(2))}>Half</Button></div>
                  </div>
                  <div>
                    <Label htmlFor="rf-reason">Reason *</Label>
                    <Input id="rf-reason" value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. duplicate booking, event cancelled" data-testid="refund-reason-input" />
                  </div>
                </div>
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <Checkbox checked={confirm} onCheckedChange={v => setConfirm(!!v)} className="mt-0.5" data-testid="refund-confirm-checkbox" />
                  <span>I confirm a refund of <strong>{money(amount)}</strong> to the original card via Stripe. This cannot be undone.</span>
                </label>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          {canRefund && refundable && (
            <Button onClick={submit} disabled={busy || !confirm || !reason.trim() || !(Number(amount) >= 0.5)} className="bg-[hsl(var(--destructive))] text-white hover:opacity-90" data-testid="refund-submit-button">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1" />} Refund {money(amount)}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
